/**
 * Where the composer's "+" / "@" / "/" menu opens.
 *
 * It opens above the box by default: docked at the bottom of a conversation
 * there is always room. On the centred new-chat hero the box sits mid-screen,
 * and a tall menu (an account with every tool) can run out of space above it,
 * where `.tabbio-main` clips it. Then the menu opens below if it fits there,
 * and otherwise toward the roomier side, capped to that space so it scrolls
 * instead of being cut off.
 */
export type ComposerMenuPlacement = {
  placement: 'above' | 'below';
  /** A height cap in px, or null when the menu fits as it is. */
  maxHeight: number | null;
};

export function resolveComposerMenuPlacement({
  spaceAbove,
  spaceBelow,
  menuHeight,
}: {
  spaceAbove: number;
  spaceBelow: number;
  menuHeight: number;
}): ComposerMenuPlacement {
  if (menuHeight <= spaceAbove) return { placement: 'above', maxHeight: null };
  if (menuHeight <= spaceBelow) return { placement: 'below', maxHeight: null };
  return spaceBelow > spaceAbove
    ? { placement: 'below', maxHeight: Math.max(0, Math.floor(spaceBelow)) }
    : { placement: 'above', maxHeight: Math.max(0, Math.floor(spaceAbove)) };
}

/**
 * The room the menu has on each side of the composer, inside the nearest
 * ancestor that clips its overflow (`.tabbio-main` on the hero), or the
 * viewport. `gap` is the space kept between the menu and the box (its 8px
 * margin) plus a little air at the clipping edge.
 */
export function measureComposerMenuSpace(anchor: HTMLElement, gap = 16) {
  let clip: DOMRect | null = null;
  for (let node = anchor.parentElement; node; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (style.overflowY !== 'visible' || style.overflow !== 'visible') {
      clip = node.getBoundingClientRect();
      break;
    }
  }
  const box = anchor.getBoundingClientRect();
  const top = Math.max(clip?.top ?? 0, 0);
  const bottom = Math.min(clip?.bottom ?? window.innerHeight, window.innerHeight);
  return { spaceAbove: box.top - top - gap, spaceBelow: bottom - box.bottom - gap };
}
